import { useUrlGenerator } from '@folklore/routes';
import {
    useQuery,
    useQueryClient,
    keepPreviousData as previousPlaceholder,
} from '@tanstack/react-query';
import { use, useCallback } from 'react';
import { useLocation, useParams } from 'wouter';

import { useApi } from '../contexts/ApiContext';
import useGame from './useGame';
import useParticipation from './useParticipation';

export function fetchPage(queryClient, api, path) {
    return queryClient.fetchQuery({
        queryKey: ['page', path],
        queryFn: ({ queryKey: [, queryParam], signal }) =>
            api.pages.findBySlug(queryParam, { signal }),
    });
}

export function prefetchPage(queryClient, api, path) {
    return queryClient.prefetchQuery({
        queryKey: ['page', path],
        queryFn: ({ queryKey: [, queryParam], signal }) =>
            api.pages.findBySlug(queryParam, { signal }),
    });
}

export function usePrefetchPage() {
    const api = useApi();
    const queryClient = useQueryClient();
    return useCallback((path) => prefetchPage(queryClient, api, path), [queryClient, api]);
}

export default function usePage(path = null, opts = null) {
    const [currentLocation] = useLocation();
    const api = useApi();
    const { keepPreviousData = true } = opts || {};
    const finalPath = path ?? currentLocation;
    const {
        data: page = null,
        isFetched = false,
        ...queryResult
    } = useQuery({
        queryKey: ['page', finalPath],
        queryFn: ({ queryKey: [, pathParam], signal }) =>
            api.pages.findByPath(pathParam, { signal }),
        suspense: false,
        placeholderData: keepPreviousData ? previousPlaceholder : undefined,
        enabled: finalPath !== null,
        ...opts,
    });

    return {
        page: (page as Page) ?? null,
        notFound: page === null && isFetched,
        isFetched,
        ...queryResult,
    };
}

export function useGamePage(opts?) {
    const {
        game: gameParam = null,
        participation: participationParam = null,
        question: questionParam = null,
    } = useParams<GamePageParams>();
    const url = useUrlGenerator();
    const queryClient = useQueryClient();
    const { page, isLoading, refetch, ...rest } = usePage(
        participationParam !== null
            ? url(questionParam !== null ? 'participation.question' : 'participation', {
                  participation: participationParam,
                  question: questionParam,
              })
            : url(questionParam !== null ? 'game.question' : 'game', {
                  game: gameParam,
                  question: questionParam,
              }),
        {
            staleTime: 0,
            refetchOnMount: 'always',
            ...opts,
        },
    );
    const { game: initialGame = null, participation: initialParticipation = null } =
        (page as GamePage) || {};
    const { id: gameId } = initialGame || {};
    const { game } = useGame(gameId, {
        initialData: initialGame,
        enabled: !isLoading,
    });

    const { id: participationId = null } = initialParticipation || {};
    const { participation } = useParticipation(participationId, {
        initialData: initialParticipation,
        enabled: !isLoading,
    });

    const reload = useCallback(() => {
        // alert('test');
        // queryClient.invalidateQueries({
        //     queryKey: ['page', url('game', { game: gameId })],
        // });
        // if (participationParam !== null) {
        //     queryClient.invalidateQueries({
        //         queryKey: ['page', url('participation', { game: participationParam })],
        //     });
        //     queryClient.removeQueries({
        //         queryKey: ['page', url('game', { game: gameId })],
        //     });
        // }
        // return refetch();
        return Promise.resolve();
    }, [queryClient, url, refetch, participationParam, gameId]);

    const clearGamePage = useCallback(() => {
        queryClient.removeQueries({
            queryKey: ['page', url('game', { game: gameId })],
        });
    }, [queryClient, gameId]);

    // console.log(participation);

    return {
        page: page as GamePage,
        game,
        participation,
        isLoading,
        reload,
        refetch,
        clearGamePage,
        ...rest,
    };
}

export function useDisplayPage(opts?) {
    const { page, ...rest } = usePage(null, opts);
    return {
        page: page as DisplayPage,
        ...rest,
    };
}
